import type { VideoFrameShaderInterfaceType, ShaderPipelineInputDefinition, ShaderPipelineInputDefinitionUniformBlock, ShaderSubpassDefinition, ShaderSubpassInput } from '@mimir/cutter/compositor/shaderContext';

export type FragmentShaderTarget = 'webgl-2.0' | 'vulkan-1.0';

export interface FragmentShader {
  code: string,
  uniformNames: string[],
}

export function generateFragmentShader(target: FragmentShaderTarget, subpass: ShaderSubpassDefinition, pipelineInputs: ShaderPipelineInputDefinition[]): FragmentShader {
  let code = `#version ${glslVersion(target)}\n`;
  code += 'precision mediump float;\n';
  const uniformNames : string[] = [];
  for (let i = 0; i < subpass.inputs.length; i++) {
    code += subpassInputDeclaration({ target, subpassInput: subpass.inputs[i], pipelineInputs, mut: { uniformNames } });
  }
  code += subpass.fragmentShader;
  code += `${attributeLayout(target, 0)} in vec2 uv;\n`;
  code += `${attributeLayout(target, 0)} out vec4 color;\n`;
  code += 'void main() {\n';
  code += '  color = fragment(uv);\n';
  code += '}\n';
  return { code, uniformNames };
}

// -- end of public api

function attributeLayout(target: FragmentShaderTarget, location: number): string {
  switch (target) {
    case 'webgl-2.0': return '';
    case 'vulkan-1.0': return `layout(location = ${location})`;
  }
}

function glslVersion(target: FragmentShaderTarget): string {
  switch (target) {
    case 'webgl-2.0': return '300 es';
    case 'vulkan-1.0': return '450';
  }
}

function samplerLayout(target: FragmentShaderTarget, bindingIndex: number): string {
  switch (target) {
    case 'vulkan-1.0': return `layout(set = 0, binding = ${bindingIndex})`;
    case 'webgl-2.0': return '';
  }
}

function declareSampler2D(
  parameters: {
    target: FragmentShaderTarget,
    name: string,
    mut: { uniformNames: string[] },
  }
): string {
  const { target, name, mut } = parameters;
  const bindingIndex = mut.uniformNames.length;
  mut.uniformNames.push(name);
  return `${samplerLayout(target, bindingIndex)} uniform sampler2D ${name};\n`;
}

function declareVideoFrameInput(
  parameters: {
    target: FragmentShaderTarget,
    name: string,
    shaderInterfaceType: VideoFrameShaderInterfaceType,
    mut: { uniformNames: string[] },
  }
): string {
  const { target, name, shaderInterfaceType, mut } = parameters;
  switch (shaderInterfaceType) {
    case 'rgba_linear_to_rgba_linear': {
      const samplerName = `${name}_rgba`;
      let source = '';
      source += declareSampler2D({ target, name: samplerName, mut });
      source += `vec4 ${name}(vec2 uv) { return texture(${samplerName}, uv); }\n`;
      return source;
    }
    case 'bgra_linear_to_rgba_linear': {
      const samplerName = `${name}_bgra`;
      let source = '';
      source += declareSampler2D({ target, name: samplerName, mut });
      source += `vec4 ${name}(vec2 uv) { return texture(${samplerName}, uv).bgra; }\n`;
      return source;
    }
    case 'bgr0_linear_to_rgba_linear': {
      const samplerName = `${name}_bgr0`;
      let source = '';
      source += declareSampler2D({ target, name: samplerName, mut });
      source += `vec4 ${name}(vec2 uv) { return vec4(texture(${samplerName}, uv).bgr, 1.0); }\n`;
      return source;
    }
    case 'yuv_planar_bt709_to_rgba_linear': {
      const lumaSamplerName = `${name}_luma`;
      const cbSamplerName = `${name}_cb`;
      const crSamplerName = `${name}_cr`;
      let source = '';
      source += declareSampler2D({ target, name: lumaSamplerName, mut });
      source += declareSampler2D({ target, name: cbSamplerName, mut });
      source += declareSampler2D({ target, name: crSamplerName, mut });
      source += `
        vec4 ${name}(vec2 uv) {\
          float y  = texture(${lumaSamplerName}, uv).r;
          float cb = texture(${cbSamplerName}, uv).r - 0.5;
          float cr = texture(${crSamplerName}, uv).r - 0.5;

          float r = y + 1.402 * cr;
          float g = y - 0.344136 * cb - 0.714136 * cr;
          float b = y + 1.772 * cb;

          float gamma = 2.2;
          return vec4(pow(vec3(r, g, b), vec3(2.2)), 1.0);
        }
      `
      return source;
    }
    case 'nv12_bt709_to_rgba_linear': {
      const lumaSamplerName = `${name}_luma`;
      const cbCrSamplerName = `${name}_cbcr`;
      let source = '';
      source += declareSampler2D({ target, name: lumaSamplerName, mut });
      source += declareSampler2D({ target, name: cbCrSamplerName, mut });
      source += `
        vec4 ${name}(vec2 uv) {\
          float y  = texture(${lumaSamplerName}, uv).r;
          vec2 cbcr = texture(${cbCrSamplerName}, uv).rg - 0.5;

          float r = y + 1.402 * cbcr.g;
          float g = y - 0.344136 * cbcr.r - 0.714136 * cbcr.g;
          float b = y + 1.772 * cbcr.r;

          float gamma = 2.2;
          return vec4(pow(vec3(r, g, b), vec3(2.2)), 1.0);
        }
      `
      return source;
    }
  }
}

function declareUniformBlock(
  parameters: {
    target: FragmentShaderTarget,
    name: string,
    pipelineInput: ShaderPipelineInputDefinitionUniformBlock,
    mut: { uniformNames: string [] },
  }
): string {
  const { target, name, pipelineInput, mut } = parameters;

  let source = '';
  if (target === 'vulkan-1.0') {
    source += `layout(set = 0, binding = ${mut.uniformNames.length}) `;
  }
  const uniformName = `${name}_Block`;
  source += `uniform ${uniformName} {\n`;
  mut.uniformNames.push(uniformName);
  for (const entry of pipelineInput.layout) {
    source += `  ${entry.type} ${entry.name};\n`
  }

  source += `} ${name};\n`;
  return source;
}

function declarePipelineInputInput(
  parameters: {
    target: FragmentShaderTarget,
    name: string,
    pipelineInput: ShaderPipelineInputDefinition,
    mut: { uniformNames: string[] },
  }
): string {
  const { target, name, pipelineInput, mut } = parameters;
  switch (pipelineInput.type) {
    case 'video_frame':
      return declareVideoFrameInput({
        target,
        name,
        shaderInterfaceType: pipelineInput.shaderInterface,
        mut
      });
    case 'uniform_block': {
      return declareUniformBlock({
        target,
        name,
        pipelineInput,
        mut,
      });
    }
  }
}

function declareSubpassOutputInput(
  parameters: {
    target: FragmentShaderTarget,
    name: string,
    mut: { uniformNames: string [] },
  }
): string {
  const { target, name, mut } = parameters;
  const samplerName = `${name}_subpass_output_rgba`;
  let source = declareSampler2D({ target, name: samplerName, mut });
  source += `vec4 ${name}(vec2 uv) { return texture(${samplerName}, uv); }\n`;
  return source;
}

function subpassInputDeclaration(
  parameters: {
    target: FragmentShaderTarget,
    subpassInput: ShaderSubpassInput,
    pipelineInputs: ShaderPipelineInputDefinition[],
    mut: { uniformNames: string[] },
  },
): string {
  const { target, subpassInput, pipelineInputs, mut } = parameters;
  switch (subpassInput.type) {
    case 'pipeline_input': {
      return declarePipelineInputInput({ target, name: subpassInput.name, pipelineInput: pipelineInputs[subpassInput.pipelineInputIndex], mut: parameters.mut });
    }
    case 'subpass_output': {
      return declareSubpassOutputInput({ target, name: subpassInput.name, mut });
    }
  }
}

